import { Button, Input, Modal, Select, Table, Tag, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";
import AddEditTrip from "./AddEditTrip";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_COLOURS = {
  sent: "blue",
  closed: "default",
  cancelled: "red",
};

/** Consent-form register: every form this school has sent out, with its response tally. */
const TripList = ({ onViewRoster }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState();
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const trips = useQuery(["/trip", status, search], async () => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (search) params.append("search", search);
    const res = await axios.get(`${serverUrl}/trip?${params.toString()}`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const refresh = () => queryClient.invalidateQueries("/trip");

  const handleCancel = async () => {
    try {
      const res = await axios.patch(
        `${serverUrl}/trip/${cancelling._id}/cancel`,
        {},
        { headers: authHeader() },
      );
      message.success(res?.data?.result?.message || "Consent form cancelled");
      setCancelling(null);
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    }
  };

  const handleRemind = async (record) => {
    try {
      const res = await axios.post(
        `${serverUrl}/trip/${record._id}/remind`,
        {},
        { headers: authHeader() },
      );
      message.success(res?.data?.result?.message || "Reminder sent");
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    }
  };

  const columns = [
    {
      title: "Consent form",
      width: 240,
      render: (record) => (
        <div>
          <div className="font-semibold">{record.title}</div>
          {record.description ? (
            <div className="text-xs text-gray-500 truncate" style={{ maxWidth: 220 }}>
              {record.description}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Dates",
      width: 180,
      render: (record) =>
        `${moment(record.from).format("DD-MM-YYYY")} to ${moment(
          record.to,
        ).format("DD-MM-YYYY")}`,
    },
    {
      title: "Classes",
      width: 160,
      render: (record) =>
        (record.classes || []).map((c) => c.name).join(", ") || "—",
    },
    {
      title: "Respond by",
      width: 130,
      render: (record) => moment(record.consent_deadline).format("DD-MM-YYYY"),
    },
    {
      title: "Over all Status",
      width: 220,
      render: (record) => (
        <div className="flex gap-2">
          <Tag color="green">{record.approved} approved</Tag>
          <Tag color="orange">{record.pending} pending</Tag>
          <Tag color="red">{record.rejected} declined</Tag>
        </div>
      ),
    },
    {
      title: "Status",
      width: 110,
      render: (record) => (
        <Tag color={STATUS_COLOURS[record.status] || "default"}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Action",
      width: 260,
      render: (record) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => onViewRoster(record._id)}>
            Roster
          </Button>
          <Button
            size="small"
            disabled={record.status !== "sent" || record.pending === 0}
            onClick={() => handleRemind(record)}
          >
            Remind
          </Button>
          <Button
            size="small"
            disabled={record.status !== "sent"}
            onClick={() => {
              setEditing(record);
              setFormOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            danger
            disabled={record.status !== "sent"}
            onClick={() => setCancelling(record)}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="pt-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex items-center justify-between pb-8">
          <div className="text-2xl text-orange-500 font-semibold">Consent Forms</div>
          <Button
            type="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            New Consent Form
          </Button>
        </div>

        <div className="flex gap-4 pb-4">
          <Input.Search
            allowClear
            placeholder="Search consent forms"
            style={{ width: 260 }}
            onSearch={setSearch}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={status}
            onChange={setStatus}
            options={[
              { label: "Sent", value: "sent" },
              { label: "Closed", value: "closed" },
              { label: "Cancelled", value: "cancelled" },
            ]}
          />
        </div>

        <Table
          rowKey="_id"
          className="rounded-2xl"
          loading={trips.isLoading}
          columns={columns}
          scroll={{ x: 1200 }}
          dataSource={trips?.data?.result?.data || []}
        />
      </div>

      <AddEditTrip
        open={formOpen}
        trip={editing}
        onClose={() => setFormOpen(false)}
        onSaved={refresh}
      />

      <Modal
        title="Cancel consent form"
        open={!!cancelling}
        onCancel={() => setCancelling(null)}
        onOk={handleCancel}
        okText="Cancel form"
        okButtonProps={{ danger: true }}
      >
        <p>
          Cancel <b>{cancelling?.title}</b>? Parents will be notified and can no
          longer respond.
        </p>
      </Modal>
    </div>
  );
};

export default TripList;
